import {
  Button,
  ButtonLink,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { PlotPortfolioSubscription } from '@/api/rest/resources/types/plotPortfolioSubscriptions';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { useJobSubscriptions } from '@/hooks/useJobSubscriptions';
import { useOrganization } from '@/hooks/useOrganization';
import { usePlotPortfolioSubscriptions } from '@/hooks/usePlotPortfolioSubscriptions';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import {
  StaffPortalTableCell,
  StaffPortalTableEmptyRow,
  StaffPortalTableHeadCell,
} from '@/pages/shared/staff-portal/components/StaffPortalTable';
import { paths } from '@/routing';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';

import { AddJobSubscriptionDialog } from './components/AddJobSubscriptionDialog';
import { AddPlotPortfolioSubscriptionDialog } from './components/AddPlotPortfolioSubscriptionDialog';
import { ViewPlotPortfolioSubscriptionDialog } from './components/ViewPlotPortfolioSubscriptionDialog';

export const OrganizationNdiSubscriptionsPage = () => {
  const { t } = useTranslation();
  const { organizationId } = useParams();
  const organization = useOrganization({ id: organizationId }).data.results[0];
  const { data: jobSubscriptionsData } = useJobSubscriptions({ org: organizationId });
  const jobSubscriptions = jobSubscriptionsData.results;
  const { data: plotPortfolioSubscriptionsData } = usePlotPortfolioSubscriptions({ org: organizationId });
  const plotPortfolioSubscriptions = plotPortfolioSubscriptionsData.results;

  const [showAddJobSubscriptionDialog, setShowAddJobSubscriptionDialog] = useState(false);
  const [showAddPlotPortfolioSubscriptionDialog, setShowAddPlotPortfolioSubscriptionDialog] = useState(false);
  const [viewPlotPortfolioSubscription, setViewPlotPortfolioSubscription] = useState<PlotPortfolioSubscription | null>(
    null,
  );

  if (isNullish(organizationId) || isNullish(organization)) {
    throw new Error(`Unknown organization with id ${organizationId}`);
  }

  return (
    <>
      <SignedInPageLayout
        items={[]}
        withCustomContentContainer
        showBreadcrumbs={false}
        navBarProps={{
          title: t('shared.ndiOrganizationSubscriptions.title', { name: organization.name }),
          showSearchBar: false,
          showFilters: false,
          children: (
            <Stack direction='row' spacing={2}>
              <ButtonLink
                to={buildPath(paths.staff.organizationNdiFinancial, { pathParams: { organizationId } })}
                variant='outline'
                leftAdornment={<Icon icon='ri-bank-line' />}
              >
                {t('shared.ndiOrganizationSubscriptions.financialButton')}
              </ButtonLink>
              <Dropdown modal={false}>
                <DropdownTrigger data-testid='add-subscription-menu-trigger' asChild>
                  <Button leftAdornment={<Icon icon='ri-add-line' />}>
                    {t('shared.ndiOrganizationSubscriptions.addButton')}
                  </Button>
                </DropdownTrigger>
                <DropdownContent side='bottom' sideOffset={10}>
                  <DropdownItem
                    data-testid='add-subscription-menu-job-subscription'
                    onClick={() => setShowAddJobSubscriptionDialog(true)}
                  >
                    {t('shared.ndiOrganizationSubscriptions.addMenu.jobSubscription')}
                  </DropdownItem>
                  <DropdownItem
                    data-testid='add-subscription-menu-plot-portfolio-subscription'
                    onClick={() => setShowAddPlotPortfolioSubscriptionDialog(true)}
                  >
                    {t('shared.ndiOrganizationSubscriptions.addMenu.plotPortfolioSubscription')}
                  </DropdownItem>
                </DropdownContent>
              </Dropdown>
            </Stack>
          ),
        }}
      >
        <Stack className='px-48 py-12 gap-9'>
          <Stack spacing={4}>
            <span className='typography-h4'>{t('shared.ndiOrganizationSubscriptions.sections.jobSubscriptions')}</span>
            <div className='rounded-2xl bg-white-100 border border-neutral-black-12'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <StaffPortalTableHeadCell>
                      {t('shared.ndiOrganizationSubscriptions.fields.product')}
                    </StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>
                      {t('shared.ndiOrganizationSubscriptions.fields.displayName')}
                    </StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>
                      {t('shared.ndiOrganizationSubscriptions.fields.plotPortfolioSubscription')}
                    </StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>
                      {t('shared.ndiOrganizationSubscriptions.fields.startDate')}
                    </StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>
                      {t('shared.ndiOrganizationSubscriptions.fields.endDate')}
                    </StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>
                      {t('shared.ndiOrganizationSubscriptions.fields.jobCreationCostCredits')}
                    </StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>
                      {t('shared.ndiOrganizationSubscriptions.fields.jobExecutionCostCredits')}
                    </StaffPortalTableHeadCell>
                    <TableHead className='w-px' />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobSubscriptions.length === 0 && (
                    <StaffPortalTableEmptyRow
                      colSpan={8}
                      message={t('shared.ndiOrganizationSubscriptions.emptyState')}
                    />
                  )}
                  {jobSubscriptions.map((jobSubscription) => (
                    <TableRow key={jobSubscription.id} className='border-b border-divider'>
                      <StaffPortalTableCell className='font-medium'>
                        {jobSubscription.job_product_display_name}
                      </StaffPortalTableCell>
                      <StaffPortalTableCell>{jobSubscription.display_name}</StaffPortalTableCell>
                      <StaffPortalTableCell>
                        <button
                          type='button'
                          className='underline text-primary-100 hover:text-primary-120'
                          onClick={() =>
                            setViewPlotPortfolioSubscription(
                              plotPortfolioSubscriptions.find(
                                (plotPortfolioSubscription) =>
                                  plotPortfolioSubscription.id === jobSubscription.ndi_plot_portfolio_subscription,
                              ) ?? null,
                            )
                          }
                        >
                          {jobSubscription.ndi_plot_portfolio_subscription_display_name}
                        </button>
                      </StaffPortalTableCell>
                      <StaffPortalTableCell>
                        {new Date(jobSubscription.start_date).toLocaleDateString()}
                      </StaffPortalTableCell>
                      <StaffPortalTableCell>
                        {new Date(jobSubscription.end_date).toLocaleDateString()}
                      </StaffPortalTableCell>
                      <StaffPortalTableCell>{jobSubscription.job_creation_cost_credits}</StaffPortalTableCell>
                      <StaffPortalTableCell>{jobSubscription.job_execution_cost_credits}</StaffPortalTableCell>
                      <TableCell className='w-px py-3 pr-4 align-middle'>
                        <ButtonLink
                          to={buildPath(paths.staff.organizationNdiSubscriptionEdit, {
                            pathParams: { organizationId, jobSubscriptionId: jobSubscription.id },
                          })}
                          variant='outline'
                          size='small'
                          leftAdornment={<Icon icon='ri-edit-line' />}
                        >
                          {t('global.ui.buttons.edit')}
                        </ButtonLink>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Stack>

          <Stack spacing={4}>
            <span className='typography-h4'>
              {t('shared.ndiOrganizationSubscriptions.sections.plotPortfolioSubscriptions')}
            </span>
            <div className='rounded-2xl bg-white-100 border border-neutral-black-12'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <StaffPortalTableHeadCell>
                      {t('shared.ndiOrganizationSubscriptions.fields.product')}
                    </StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>
                      {t('shared.ndiOrganizationSubscriptions.fields.displayName')}
                    </StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>
                      {t('shared.ndiOrganizationSubscriptions.fields.startDate')}
                    </StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>
                      {t('shared.ndiOrganizationSubscriptions.fields.endDate')}
                    </StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>
                      {t('shared.ndiProducts.fields.plotCreationCostCredits')}
                    </StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>{t('shared.ndiProducts.fields.maxPlots')}</StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>{t('shared.ndiProducts.fields.maxHectares')}</StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>{t('shared.ndiProducts.fields.totalPlots')}</StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>{t('shared.ndiProducts.fields.totalHectares')}</StaffPortalTableHeadCell>
                    <StaffPortalTableHeadCell>
                      {t('shared.ndiProducts.fields.totalJobSubscriptions')}
                    </StaffPortalTableHeadCell>
                    <TableHead className='w-px' />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plotPortfolioSubscriptions.length === 0 && (
                    <StaffPortalTableEmptyRow
                      colSpan={11}
                      message={t('shared.ndiOrganizationSubscriptions.emptyState')}
                    />
                  )}
                  {plotPortfolioSubscriptions.map((plotPortfolioSubscription) => (
                    <TableRow key={plotPortfolioSubscription.id} className='border-b border-divider'>
                      <StaffPortalTableCell className='font-medium'>
                        {plotPortfolioSubscription.plot_portfolio_product_name}
                      </StaffPortalTableCell>
                      <StaffPortalTableCell>{plotPortfolioSubscription.display_name}</StaffPortalTableCell>
                      <StaffPortalTableCell>
                        {new Date(plotPortfolioSubscription.start_date).toLocaleDateString()}
                      </StaffPortalTableCell>
                      <StaffPortalTableCell>
                        {new Date(plotPortfolioSubscription.end_date).toLocaleDateString()}
                      </StaffPortalTableCell>
                      <StaffPortalTableCell>
                        {plotPortfolioSubscription.plot_creation_cost_credits}
                      </StaffPortalTableCell>
                      <StaffPortalTableCell>
                        {plotPortfolioSubscription.max_plots ?? t('shared.ndiProducts.noLimit')}
                      </StaffPortalTableCell>
                      <StaffPortalTableCell>
                        {plotPortfolioSubscription.max_hectares ?? t('shared.ndiProducts.noLimit')}
                      </StaffPortalTableCell>
                      <StaffPortalTableCell>{plotPortfolioSubscription.total_plots}</StaffPortalTableCell>
                      <StaffPortalTableCell>{plotPortfolioSubscription.total_hectares}</StaffPortalTableCell>
                      <StaffPortalTableCell>{plotPortfolioSubscription.total_job_subscriptions}</StaffPortalTableCell>
                      <TableCell className='w-px py-3 pr-4 align-middle'>
                        <ButtonLink
                          to={buildPath(paths.staff.organizationNdiPlotPortfolioSubscriptionEdit, {
                            pathParams: { organizationId, plotPortfolioSubscriptionId: plotPortfolioSubscription.id },
                          })}
                          variant='outline'
                          size='small'
                          leftAdornment={<Icon icon='ri-edit-line' />}
                        >
                          {t('global.ui.buttons.edit')}
                        </ButtonLink>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Stack>
        </Stack>
      </SignedInPageLayout>
      <AddJobSubscriptionDialog
        open={showAddJobSubscriptionDialog}
        onClose={() => setShowAddJobSubscriptionDialog(false)}
        organizationId={organizationId}
      />
      <AddPlotPortfolioSubscriptionDialog
        open={showAddPlotPortfolioSubscriptionDialog}
        onClose={() => setShowAddPlotPortfolioSubscriptionDialog(false)}
        organizationId={organizationId}
      />
      <ViewPlotPortfolioSubscriptionDialog
        plotPortfolioSubscription={viewPlotPortfolioSubscription}
        onClose={() => setViewPlotPortfolioSubscription(null)}
      />
    </>
  );
};
